import "./App.css";

import {
  Navigate,
  BrowserRouter as Router,
  Routes,
  Route,
} from "react-router-dom";
import Login from "./pages/Login";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { auth } from "./firebase-config";
import PageNotFound from "./pages/errors/NotFound";
import { Box, Center, Container, Flex, Loader, MantineProvider, Stack, Title, Text, Button, ActionIcon, Tooltip } from "@mantine/core";
import { IconMailOpened, IconSearch } from "@tabler/icons";
import { openSpotlight } from "@mantine/spotlight";
import FindSomeoneSpotlight from "./components/FindSomeoneSpotlight";
import * as apiService from "./api-service";
import { firestore } from "./firebase-config";
import { doc, onSnapshot } from "firebase/firestore";
import { isSignInWithEmailLink } from "firebase/auth";
import Dashboard from "./pages/dashboard";
import {
  AppShell,
  Navbar,
  Header,
  Burger,
  useMantineTheme,
  MediaQuery,
} from "@mantine/core";
import { AppNavBar } from "./components/AppNavBar";
import { ReactComponent as CellSoftwareLogo } from "./images/cell-software-logo.svg";
import { LoadingImage } from "./components/LoadingImage";
import { getContrastColor, lighten, buildColorShades } from "./utils/color";
import { listenForForegroundPush, syncPushSubscriptions } from "./services/PushNotifications";
import { ShowNotificationInfo } from "./components/Notifications";
import { hasExtraRole, getExtraRoles } from "./services/UserRoles";

// Dashboard/Login/PageNotFound stay as regular imports - they're needed immediately for the
// overwhelming majority of page loads. Everything below is a secondary page reached by
// navigating away from Dashboard first, and several are feature-gated (Automations, TestRail,
// Jira, SMS, Durian verification) so most users never open them at all - lazy-loading these means
// their code (and their own dependencies) is only downloaded if/when a route is actually visited,
// instead of bloating the very first page load for everyone.
const Automations = lazy(() => import("./pages/automations/Automations"));
const AutomationDetails = lazy(() => import("./pages/automations/AutomationDetails"));
const SMSMonitor = lazy(() => import("./pages/tools/sms"));
const Employees = lazy(() => import("./pages/Employees/Employees"));
const Clients = lazy(() => import("./pages/Clients/Clients"));
const BusinessEdit = lazy(() => import("./pages/business/BusinessEdit"));
const AuditLog = lazy(() => import("./pages/AuditLog/AuditLog"));
const Action = lazy(() => import("./pages/action"));
const CardArtworkVerification = lazy(() => import("./pages/durian/verification/card-artwork"));
const TermsConditionsVerification = lazy(() => import("./pages/durian/verification/terms-and-conditions"));
const ErdnussCardArtworkVerification = lazy(() => import("./pages/erdnuss/verification/card-artwork"));
const ErdnussTermsConditionsVerification = lazy(() => import("./pages/erdnuss/verification/terms-and-conditions"));
const Testrail = lazy(() => import("./pages/testrail/testrail"));
const JiraBoards = lazy(() => import("./pages/jira/boards/Boards"));
const Booker2 = lazy(() => import("./pages/booker/booker"));
const Posts = lazy(() => import("./pages/Posts/Posts"));
const PostDetail = lazy(() => import("./pages/Posts/PostDetail"));
const HarvestForecast = lazy(() => import("./pages/harvest-forecast/HarvestForecast"));
const Documents = lazy(() => import("./pages/documents/Documents"));
const HarvestOAuthCallback = lazy(() => import("./pages/harvest-forecast/oauth-callback"));
const Runway = lazy(() => import("./pages/runway/Runway"));
const RunwayShare = lazy(() => import("./pages/runway/RunwayShare"));
const MyBuilds = lazy(() => import("./pages/runway/MyBuilds"));
const Feedback = lazy(() => import("./pages/feedback/Feedback"));
const Udid = lazy(() => import("./pages/Udid"));

// Shown at "/" instead of the Dashboard when someone's already signed in on this device but the
// URL is still carrying a passwordless email sign-in link (?mode=signIn&oobCode=...) - previously
// this URL was silently ignored (App only ever looked at auth state, never the query string), so
// re-opening an old sign-in email while already logged in just quietly showed the Dashboard with
// no indication the link itself wasn't actually doing anything. The link is genuinely one-time-use
// (Login.js already handles a stale/expired code correctly for a signed-out visitor) - this only
// covers the case where someone's session was still valid, so there was nothing to "complete".
function AlreadySignedInLinkNotice({ onContinue }) {
  return (
    <Container>
      <Center style={{ minHeight: "60vh" }}>
        <Stack align="center" spacing="xs" sx={{ maxWidth: 420 }}>
          <IconMailOpened size={40} stroke={1.5} color="var(--app-accent-color, #228be6)" />
          <Title order={3} align="center">
            You&apos;re already signed in
          </Title>
          <Text c="dimmed" align="center" size="sm">
            That sign-in link doesn&apos;t need to be used again - you&apos;re still signed in on this device.
          </Text>
          <Button mt="sm" onClick={onContinue}>
            Continue to the Portal
          </Button>
        </Stack>
      </Center>
    </Container>
  );
}

export default function App() {
  const [loading, setLoading] = useState(true);

  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserClaims, setCurrentUserClaims] = useState(null);
  const [currentUserToken, setCurrentUserToken] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [companyData, setcompanyData] = useState(null);

  // Captured once on first render rather than recomputed on every render - "Continue" below
  // strips the query string via replaceState, and this must NOT flip back to true afterward just
  // because window.location.href still happens to match again on some unrelated re-render.
  const [isEmailLinkUrl, setIsEmailLinkUrl] = useState(() => isSignInWithEmailLink(auth, window.location.href));

  const theme = useMantineTheme();
  const [opened, setOpened] = useState(false);

  const [navBarButton, setNavBarButton] = useState();

  // Track whether the user is logged in or not
  useEffect(() => {
    // Tracks whichever company-doc listener is currently live, so it can be torn down before a
    // new one is created (auth state can fire more than once in a session - e.g. a token
    // refresh) and on unmount - previously this was never captured, so every re-fire left its
    // old listener running forever, stacking up duplicate live subscriptions over time.
    let unsubscribeCompanyDoc = null;

    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (unsubscribeCompanyDoc) {
        unsubscribeCompanyDoc();
        unsubscribeCompanyDoc = null;
      }

      if (user) {
        user.getIdToken().then((tokenId) => {
          setCurrentUserToken(tokenId);
          apiService
            .getUser({
              userIdToken: tokenId,
              uid: user.uid,
            })
            .then((data) => {
              if (data["claims"]["companyId"] && data["claims"]["access"]) {
                const companyId = data["claims"]["companyId"];

                setCurrentUserClaims(data["claims"]);

                unsubscribeCompanyDoc = onSnapshot(doc(firestore, "companies", companyId), (doc) => {
                  setcompanyData(doc);
                  setCompanyId(companyId);
                  setCurrentUser(user);
                  setLoading(false);
                });
              } else {
                setCurrentUser(null);
                setLoading(false);
              }
            })
            .catch((e) => {
              console.error(e);
              setCurrentUser(null);
              setLoading(false);
            });
        });
      } else {
        // Previously only setLoading(false) ran here - currentUser/companyData/companyId/
        // currentUserClaims were left holding whatever they were before sign-out, so a route
        // gated on `currentUser` truthy (which updates first, in the same tick) could briefly
        // re-render against a stale companyData/claims pairing left over from the old session.
        setCurrentUser(null);
        setcompanyData(null);
        setCompanyId(null);
        setCurrentUserClaims(null);
        setCurrentUserToken(null);
        setLoading(false);
        // alert("Session has timed out. Please login again.");
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeCompanyDoc) {
        unsubscribeCompanyDoc();
      }
    };
  }, []);

  // Keeps the session's token and claims fresh for as long as the tab stays open, instead of
  // only ever reading them once at sign-in (above). Firebase ID tokens expire after an hour, so
  // without this every backend call kept sending an increasingly stale token past that point -
  // and if an admin changed someone's role/claims while they were already logged in, the nav bar
  // and route gating never picked it up until they signed out and back in.
  useEffect(() => {
    const unsubscribeIdToken = auth.onIdTokenChanged(async (user) => {
      if (!user) {
        return;
      }
      try {
        const token = await user.getIdToken();
        setCurrentUserToken(token);
        const data = await apiService.getUser({ userIdToken: token, uid: user.uid });
        setCurrentUserClaims(data["claims"]);
      } catch (e) {
        console.error("Failed to refresh session token/claims", e);
      }
    });

    // onIdTokenChanged alone only fires when the SDK actually rotates the token, which normally
    // happens close to the hour mark - forcing a refresh periodically means a claims change shows
    // up within this interval instead of requiring a full re-login to notice.
    const refreshInterval = setInterval(() => {
      auth.currentUser?.getIdToken(true);
    }, 15 * 60 * 1000);

    return () => {
      unsubscribeIdToken();
      clearInterval(refreshInterval);
    };
  }, []);

  // A push that arrives while this tab already has focus never shows as a native OS notification
  // (see PushNotifications.js's own comment on listenForForegroundPush) - shown as an in-app toast
  // here instead, app-wide rather than only on the Dashboard, so it still surfaces if someone's
  // sat on a different page when a post goes out. Registered once regardless of sign-in state -
  // listenForForegroundPush itself is a no-op in unsupported browsers/contexts.
  useEffect(() => {
    listenForForegroundPush((payload) => {
      ShowNotificationInfo(payload.notification?.title ?? "New post published", payload.notification?.body ?? "");
    });
  }, []);

  // Keeps this device's push topic subscriptions matching the signed-in person's current roles (a
  // post restricted to a role is only pushed to devices subscribed to that role's topic). Keyed on
  // the roles themselves rather than the token, which refreshes hourly for no reason here. A no-op
  // unless notifications were already enabled on this device.
  const extraRolesKey = JSON.stringify(getExtraRoles(currentUserClaims));
  useEffect(() => {
    if (!currentUserToken || !companyId || !currentUserClaims) return;
    syncPushSubscriptions({ userIdToken: currentUserToken, companyId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, extraRolesKey]);

  // Memoized so that `company` is referentially stable across renders that don't touch
  // companyData - otherwise every downstream useMemo/useEffect keyed on `company` (there are
  // several, spread across the tree this object gets passed into) would see a "new" object on
  // every single App render and recompute for nothing.
  const company = useMemo(() => companyData?.data() ?? {}, [companyData]);
  const accent = company["accent"] || null;

  // Exposed as a CSS custom property on the document root (rather than read from React/Mantine
  // theme context) so that anything rendered outside the normal component tree - like
  // notifications, which Mantine portals straight onto document.body - can still pick up the
  // company's accent colour without needing access to theme context at all.
  useEffect(() => {
    document.documentElement.style.setProperty("--app-accent-color", accent || "#228be6");
  }, [accent]);

  if (loading)
    return (
      <Center
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <Loader size="xl" />
      </Center>
    );

  // Buttons rendered into the header (top-right) need to stay legible against whatever
  // accent colour the company has chosen, so their colours are derived automatically.
  const headerButtonBg = accent ? getContrastColor(accent) : "#ffffff";
  const headerButtonText = accent ? (headerButtonBg === "#ffffff" ? accent : "#ffffff") : undefined;
  const headerButtonHoverBg = accent
    ? headerButtonBg === "#ffffff"
      ? lighten(accent, 0.92)
      : lighten(headerButtonBg, 0.15)
    : undefined;

  // Same idea as the header button colours above - the burger needs to stay legible against
  // whatever the header's actual background is. With a company accent set, that's the accent
  // colour itself, so contrast off that; with no accent, the header falls back to Mantine's own
  // light/dark header background, so contrast off the colour scheme instead.
  const headerBurgerColor = accent
    ? getContrastColor(accent)
    : theme.colorScheme === "dark"
    ? theme.colors.dark[0]
    : theme.colors.gray[6];

  // Every component that reads from the theme (Buttons, Switches, Tabs, ActionIcons, etc.) picks
  // up the company's own accent colour as its main tint, instead of Mantine's default blue. This
  // wraps the whole app (not just the header) so it applies everywhere, and simply falls through
  // to Mantine's defaults when a company hasn't set an accent colour yet.
  //
  // Signed out (no session yet, or just logged out), this also forces light mode regardless of
  // whatever dark/light preference is stored from a previous session - the login screen shows a
  // company's own logo/brand colour instead, so it shouldn't also be showing up in whatever dark
  // mode state was left over from someone else's session on this device.
  const isSignedOut = !currentUser;
  // Runway's public share page (src/pages/runway/RunwayShare.js) is reachable with no session at
  // all, but it's also just as reachable by someone who happens to already be signed into the
  // Portal in that same browser (e.g. testing their own share link) - in both cases it's meant to
  // read as a standalone branded page, not the full app shell wrapped around it, so this is a
  // route check rather than an auth check. There's no in-app link that client-side-navigates here
  // (Copy Share Link only ever copies text), so this is always reached via a fresh page load,
  // meaning a plain read of window.location.pathname - no router hook needed - is never stale.
  // Matches "/runway/share" itself (an incomplete/malformed link - no companyId/shareToken at
  // all, which just falls through to the app's catch-all 404) as well as "/runway/share/..." -
  // not just startsWith("/runway/share/"), which missed the bare path since it has no trailing
  // slash to match against.
  const isRunwayShareRoute =
    window.location.pathname === "/runway/share" || window.location.pathname.startsWith("/runway/share/");
  const accentThemeOverride = {
    ...(accent ? { colors: { accent: buildColorShades(accent) }, primaryColor: "accent" } : {}),
    ...(isSignedOut ? { colorScheme: "light" } : {}),
  };

  return (
    <MantineProvider inherit theme={accentThemeOverride}>
    <Router>
      <FindSomeoneSpotlight
        companyId={companyId}
        companyData={company}
        userClaims={currentUserClaims}
        currentUser={currentUser}
        userToken={currentUserToken}
      >
      <AppShell
        styles={{
          main: {
            background:
              !isSignedOut && theme.colorScheme === "dark"
                ? theme.colors.dark[8]
                : theme.colors.gray[0],
          },
        }}
        hidden={currentUserClaims === null || isRunwayShareRoute}
        navbarOffsetBreakpoint="sm"
        asideOffsetBreakpoint="sm"
        header={
          <Header
            height={{ base: 76 }}
            p="md"
            sx={{ backgroundColor: company["accent"] || undefined }}
          >
            {/* justify="flex-start" (rather than "space-between") plus ml="auto" on the button
                slot below - so the logo always sits immediately after the burger on the left,
                regardless of whether a page sets a right-hand nav button. With space-between,
                the logo drifted to the far right on pages (like Dashboard) that don't set one,
                since it ended up as the last of only two flex children.

                The logo slot is the only one allowed to shrink (flex: 1 1 auto + minWidth: 0,
                with its image/svg height set via clamp() rather than a fixed px value) - the
                burger and the button slot are both flexShrink: 0, so on a narrow screen it's
                always the logo that gives up space, never the button getting squeezed off the
                right edge of the header. */}
            <Flex justify="flex-start" align="center" wrap="nowrap" style={{ height: "100%" }}>
              <MediaQuery largerThan="sm" styles={{ display: "none" }}>
                <Burger
                  opened={opened}
                  onClick={() => setOpened((o) => !o)}
                  size="sm"
                  color={headerBurgerColor}
                  mr="xl"
                  sx={{ flexShrink: 0 }}
                />
              </MediaQuery>

              <Box sx={{ flex: "1 1 auto", minWidth: 0, overflow: "hidden", display: "flex", alignItems: "center" }}>
                {company["logo_header"] ? (
                  <LoadingImage
                    src={company["logo_header"]}
                    eager
                    height="clamp(28px, 9vw, 44px)"
                    width="auto"
                    fit="contain"
                    style={{ display: "block" }}
                    // wrapperSx targets the Box LoadingImage itself renders as the direct child of
                    // our flex row above - that's the actual flex item whose automatic min-width
                    // needs zeroing out. minWidth:0 on our own flex container (above) only stops
                    // IT from being pushed wider by ITS parent; left at the default of "auto", a
                    // flex item's own min-width still equals its min-content size (the logo's full
                    // intrinsic/un-shrunk width for a replaced element like an image), which is
                    // exactly what was clipping instead of shrinking.
                    wrapperSx={{ minWidth: 0, maxWidth: "100%" }}
                  />
                ) : (
                  <CellSoftwareLogo
                    style={{ display: "block", height: "clamp(28px, 9vw, 44px)", width: "auto", minWidth: 0 }}
                  />
                )}
              </Box>
              {!isSignedOut && (
                <Tooltip label="Find Someone (Ctrl/Cmd+K)" withArrow>
                  <ActionIcon
                    variant="subtle"
                    onClick={() => openSpotlight()}
                    sx={{ flexShrink: 0, color: headerBurgerColor }}
                    mr="sm"
                  >
                    <IconSearch size="1.1rem" />
                  </ActionIcon>
                </Tooltip>
              )}
              <Box ml="auto" sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
                <MantineProvider
                  inherit
                  theme={{
                    components: {
                      Button: {
                        styles: {
                          root: {
                            backgroundColor: headerButtonBg,
                            color: headerButtonText,
                            "&:hover": {
                              backgroundColor: headerButtonHoverBg,
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  {navBarButton}
                </MantineProvider>
              </Box>
            </Flex>
          </Header>
        }
        navbar={
          <Navbar
            hiddenBreakpoint="sm"
            hidden={!opened}
            width={{ sm: 300, lg: 300 }}
          >
            {/* Gated on currentUser too, not just currentUserClaims - during sign-in, claims are
                set (via apiService.getUser) a tick before currentUser itself is (which only
                happens once the company doc's onSnapshot first fires, see the auth state effect
                above). Rendering AppNavBar/UserButton in that gap crashed on
                currentUser.displayName being read off null. */}
            {currentUser && currentUserClaims && (
              <AppNavBar
                currentUser={currentUser}
                userClaims={currentUserClaims}
                companyData={company}
                isMenuOpen={opened}
                closeMenu={() => setOpened(false)}
                userToken={currentUserToken}
                companyId={companyId}
              />
            )}
          </Navbar>
        }
        // layout="alt"
        // footer={<AppFooter />}
      >
        {/* The available paths on the site - wrapped in Suspense since most of these routes are
            now lazy-loaded (see the imports above); this fallback only ever shows briefly while a
            route's own code downloads, not while its data loads (each page handles that itself). */}
        <Suspense
          fallback={
            <Center
              style={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <Loader size="xl" />
            </Center>
          }
        >
        <Routes>
          {/* Runway's public share page - reachable whether or not anyone is signed in (it's a
              password-gated link, not a session-gated one - see RunwayShare.js). Deliberately not
              wrapped in a currentUser check like every other route below. */}
          <Route path="/runway/share/:companyId/:shareToken" element={<RunwayShare />} />

          {/* login only accessible when use is logged out */}

          {/* Dashboard */}
          {currentUser ? (
            isEmailLinkUrl ? (
              <Route
                path="/"
                element={
                  <AlreadySignedInLinkNotice
                    onContinue={() => {
                      window.history.replaceState({}, document.title, window.location.pathname);
                      setIsEmailLinkUrl(false);
                    }}
                  />
                }
              />
            ) : currentUserClaims["access"] !== "client" ? (
              <Route
                path="/"
                element={
                  <Dashboard
                    companyId={companyId}
                    companyData={company}
                    currentUser={currentUser}
                    userClaims={currentUserClaims}
                    userToken={currentUserToken}
                  />
                }
              />
            ) : (
              <Route path="/" element={<Navigate to="/automations" />} />
            )
          ) : null}

          {/* Post detail - same audience as the Dashboard itself (anyone signed in who isn't a
              client), since this is just a direct/bookmarkable/shareable view of one of the same
              posts the Dashboard already lists. PostDetail.js re-derives visibility itself
              (draft/scheduled/Durian-only checks) rather than trusting the URL alone. */}
          {currentUser && currentUserClaims["access"] !== "client" && (
            <Route
              path="/post/:id"
              element={
                <PostDetail
                  companyId={companyId}
                  currentUser={currentUser}
                  userClaims={currentUserClaims}
                />
              }
            />
          )}

          {!currentUser && (
            <Route path="/" element={<Login />} />
          )}

          {currentUser && currentUserClaims["access"] !== "client" && (
            <>
              <Route
                path="/booker"
                element={
                  <Booker2
                    userToken={currentUserToken}
                    companyData={company}
                    companyId={companyId}
                    userClaims={currentUserClaims}
                    currentUser={currentUser}
                    navButton={setNavBarButton}
                  />
                }
              />
              {/* Deep link to a specific day (e.g. /booker/2026-09-08) - see useParams() in
                  booker.js for how the :date segment lands the view on that exact day rather than
                  just the current week. Kept as its own Route (rather than an optional ":date?"
                  segment on the one above) since react-router-dom 6.4 doesn't support optional
                  path segments yet - both routes render the exact same element either way. */}
              <Route
                path="/booker/:date"
                element={
                  <Booker2
                    userToken={currentUserToken}
                    companyData={company}
                    companyId={companyId}
                    userClaims={currentUserClaims}
                    currentUser={currentUser}
                    navButton={setNavBarButton}
                  />
                }
              />
            </>
          )}

          {/* Automation */}
          {currentUser &&
            company["azure"] &&
            (company["azure"]["automation_enabled"] ?? false) && (
              <Route
                path="/automations"
                element={
                  <Automations
                    userToken={currentUserToken}
                    companyId={companyId}
                    userClaims={currentUserClaims}
                  />
                }
              />
            )}
          {currentUser &&
            company["azure"] &&
            (company["azure"]["automation_enabled"] ?? false) && (
              <Route
                path="/automations/:id"
                element={
                  <AutomationDetails
                    userToken={currentUserToken}
                    companyId={companyId}
                  />
                }
              />
            )}
          {currentUser &&
            company["azure"] &&
            (company["azure"]["automation_enabled"] ?? false) && (
              <Route
                path="/automations/:id"
                element={
                  <AutomationDetails
                    userToken={currentUserToken}
                    companyId={companyId}
                  />
                }
              />
            )}

          {/* TestRail */}

          {currentUser && company["testrail"]?.["enabled"] && (
            <Route
              path="/testrail"
              element={
                <Testrail
                  userToken={currentUserToken}
                  companyId={companyId}
                  userClaims={currentUserClaims}
                />
              }
            />
          )}

          {/* JIRA BOARDS */}

          {currentUser &&
            (company["jira"]?.["boards_enabled"] ?? false) && (
              <Route
                path="/jira"
                element={
                  <JiraBoards
                    userToken={currentUserToken}
                    companyId={companyId}
                    userClaims={currentUserClaims}
                  />
                }
              />
            )}

          {/* Harvest & Forecast */}

          {currentUser &&
            (company["harvest_forecast"]?.["harvest_enabled"] ||
              company["harvest_forecast"]?.["forecast_enabled"]) && (
              <Route
                path="/harcast"
                element={
                  <HarvestForecast
                    userToken={currentUserToken}
                    companyId={companyId}
                    companyData={companyData}
                    currentUser={currentUser}
                    navButton={setNavBarButton}
                  />
                }
              />
            )}

          {/* Documents - the trailing /* splat lets a deep link like /documents/Folder/Sub/file.pdf
              resolve straight to that folder or file; Documents.js reads the splat itself via
              useParams()["*"] to figure out what's being asked for. */}
          {currentUser && (
            <Route
              path="/documents/*"
              element={
                <Documents
                  companyId={companyId}
                  companyData={company}
                  currentUser={currentUser}
                  userClaims={currentUserClaims}
                  userToken={currentUserToken}
                />
              }
            />
          )}

          {/* Runway - app-build distribution, admin/super and the "developer" sub-role only (see
              the original request). Not shown to a plain "employee" or a client - clients get
              their own, separate MyBuilds route below instead. A single 3-pane page (Projects |
              Versions | Builds, mirroring the Jira Boards layout) rather than a separate
              project-detail route - selection is kept as local component state instead of a URL
              param. */}
          {currentUser &&
            (currentUserClaims["access"] === "admin" ||
              currentUserClaims["access"] === "super" ||
              hasExtraRole(currentUserClaims, "developer")) && (
              <Route
                path="/runway"
                element={
                  <Runway
                    companyId={companyId}
                    companyData={company}
                    currentUser={currentUser}
                    userClaims={currentUserClaims}
                    userToken={currentUserToken}
                  />
                }
              />
            )}

          {/* Feedback/Roadmap - an internal Jira-style board for bugs/features/suggestions (see
              the original request). Employees only - clients never see this at all. */}
          {currentUser && currentUserClaims["access"] !== "client" && (
            <Route
              path="/feedback"
              element={
                <Feedback
                  companyId={companyId}
                  companyData={company}
                  currentUser={currentUser}
                  userClaims={currentUserClaims}
                  userToken={currentUserToken}
                />
              }
            />
          )}

          {/* My Builds - the client-facing counterpart to Runway above. Only ever shows whichever
              single build a developer/admin/super has pinned this client to, per project - see
              MyBuilds.js and get-my-assignments.js. */}
          {currentUser && currentUserClaims["access"] === "client" && (
            <Route path="/my-builds" element={<MyBuilds companyId={companyId} userToken={currentUserToken} />} />
          )}

          {/* Get Device UDID - lets anyone signed in (employee or client) find their own device's
              UDID to add to an Ad-Hoc distribution build's provisioning profile, via Apple's
              Profile Service protocol. See src/pages/Udid.js and functions/express/routes/udid/*. */}
          {currentUser && (
            <Route path="/udid" element={<Udid companyId={companyId} userToken={currentUserToken} />} />
          )}

          {/* Harvest OAuth callback - reachable any time a session exists, regardless of the
              enabled flags above, since landing here is what completes turning a connection on
              in the first place. */}
          {currentUser && (
            <Route
              path="/harvest/oauth/callback"
              element={
                <HarvestOAuthCallback userToken={currentUserToken} companyId={companyId} />
              }
            />
          )}

          {/* SMS Messaging */}
          {currentUser &&
            currentUserClaims["access"] !== "client" &&
            company["sms"]?.["enabled"] && (
              <Route
                path="/sms-messages"
                element={
                  <SMSMonitor
                    userToken={currentUserToken}
                    companyId={companyId}
                    companyData={companyData}
                  />
                }
              />
            )}

          {/* Posts */}
          {currentUser &&
            (currentUserClaims["access"] === "admin" || currentUserClaims["access"] === "super") && (
              <Route
                path="/posts"
                element={
                  <Posts
                    companyId={companyId}
                    currentUser={currentUser}
                    navButton={setNavBarButton}
                  />
                }
              />
            )}

          {/* Employees */}
          {currentUser && currentUserClaims["access"] !== "client" && (
            <Route
              path="/employees"
              element={
                <Employees
                  userToken={currentUserToken}
                  userId={currentUser.uid}
                  companyId={companyId}
                  companyData={companyData}
                  userAccess={currentUserClaims["access"]}
                  navButton={setNavBarButton}
                />
              }
            />
          )}

          {/* Clients */}
          {currentUser && currentUserClaims["access"] !== "client" && (
            <Route
              path="/clients"
              element={
                <Clients
                  userId={currentUser.uid}
                  userToken={currentUserToken}
                  companyId={companyId}
                  companyData={companyData}
                  userAccess={currentUserClaims["access"]}
                  navButton={setNavBarButton}
                />
              }
            />
          )}

          {currentUser && hasExtraRole(currentUserClaims, "durian") && (
            <>
              <Route
                path="/verification/card-artwork"
                element={
                  <CardArtworkVerification companyData={company} />
                }
              />
              <Route
                path="/verification/terms-and-conditions"
                element={
                  <TermsConditionsVerification companyData={company} userToken={currentUserToken} />
                }
              />
            </>
          )}

          {/* Erdnuss's own copies of the two verification pages, checked against Google Pay's rules
              rather than Apple Pay's. Distinct paths from Durian's, since the nav marks a link
              active by exact path. */}
          {currentUser && hasExtraRole(currentUserClaims, "erdnuss") && (
            <>
              <Route path="/erdnuss/verification/card-artwork" element={<ErdnussCardArtworkVerification />} />
              <Route
                path="/erdnuss/verification/terms-and-conditions"
                element={<ErdnussTermsConditionsVerification userToken={currentUserToken} />}
              />
            </>
          )}

          {/* Business Edit */}
          {currentUser &&
            (currentUserClaims["access"] === "admin" ||
              currentUserClaims["access"] === "super") && (
              <Route
                path="/company/edit"
                element={
                  <BusinessEdit
                    userToken={currentUserToken}
                    companyId={companyId}
                    companyData={company}
                    currentUser={currentUser}
                  />
                }
              />
            )}

          {/* Audit Log */}
          {currentUser &&
            (currentUserClaims["access"] === "admin" ||
              currentUserClaims["access"] === "super") && (
              <Route
                path="/audit-log"
                element={
                  <AuditLog
                    companyId={companyId}
                    companyData={company}
                  />
                }
              />
            )}

          {/* ACTION */}
          <Route path="/action" element={<Action />} />

          {/* Invalid path go to a 404 page */}
          {currentUser !== null ? (
            <Route path="*" element={<PageNotFound />} />
          ) : (
            <Route path="*" element={<Login />} />
          )}
        </Routes>
        </Suspense>
      </AppShell>
      </FindSomeoneSpotlight>
    </Router>
    </MantineProvider>
  );
}